// src/components/header/header.tsx
import React from 'react';
import { FiMenu } from 'react-icons/fi';

import {
  HeaderSpacer,
  HeaderContainer,
  HamburgerContainer,
  RightActions,
  IconButton,
  CancelButton,
} from './header.styled';

import { useHeader } from './header.hooks';

import UrgentMessageModal from '../sidebar/urgent-message-modal/urgent-message-modal';
import ScheduleMeetingModal from '../sidebar/schedule-meeting-modal/schedule-meeting-modal';
import Sidebar from '../sidebar/sidebar';
import { prefetchNotesMeta } from '../sidebar/sidebar.hooks';
import NotificationCollection from './modals/notification-collection/notification-collection';
import { useAuth } from '../../../contexts/auth/auth-context';
import { useNotifications } from '../../../contexts/notification-context';
import { AppAvatar } from '../../../components/avatar/avatar';
import { IconBtn } from '../sidebar/sidebar.styled';
import { smsApi } from '../../../api/sms/sms';
import { useBackHandler } from '../../../platform/use-back-handler';

interface HeaderProps {
  onNotificationClick?: () => void;
  onProfileClick?: () => void;
  onCreateJournal?: () => void;
  onBackNavigate?: () => void;
  showBackButton?: boolean;
  isChangePasswordTab?: boolean;
  children?: React.ReactNode;
  disableSidebar?: boolean;

  // ✅ NEW: fullscreen recording cancel
  showCancelButton?: boolean;
  onCancel?: () => void;

  // ✅ Recording safety: controls hamburger behavior during fullscreen recording
  /** true = actively recording → hamburger fully disabled */
  isRecordingActive?: boolean;
  /** Called when hamburger is tapped in paused state (show discard modal) */
  onMenuBlocked?: (() => void) | undefined;

  /** Called when a notification is clicked to open the conversation and optionally scroll to a message */
  onOpenConversation?: ((messageId?: string) => void) | undefined;

  /** Called when the listen pill is tapped on a message_received notification — opens conversation + auto-plays */
  onListenInConversation?: ((messageId: string) => void) | undefined;

  /** Desktop mode: changes header layout */
  isDesktop?: boolean;
  /** Toggle sidebar open/collapsed (desktop only) */
  onToggleSidebar?: () => void;

  /** Conversation ID passed from parent (avoids duplicate useConversation call) */
  conversationId?: string | undefined;

  /** Called when "View in conversation" is clicked in files drawer */
  onViewFileInConversation?: (messageId: string, conversationId: string) => void;

  /** Called when a file is deleted from the files drawer */
  onFileDeleted?: (fileId: string) => void;

  /** Called when the user avatar is clicked (desktop: opens profile settings externally) */
  onAvatarClick?: (() => void) | undefined;
}

const NotificationBell: React.FC<{ count?: number }> = ({ count = 0 }) => (
  <div style={{ position: 'relative', display: 'inline-block' }}>
    <img
      src="/notification-bell.svg"
      alt="Notifications"
      width={20}
      height={24}
      style={{ display: 'block' }}
    />
    {count > 0 && (
      <span
        style={{
          position: 'absolute',
          top: -4,
          right: -8,
          minWidth: 16,
          height: 16,
          borderRadius: 999,
          background: '#5E84ED',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 4px',
          boxSizing: 'border-box',
          border: '2px solid #1a1a1c',
        }}
      >
        <span
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 600,
            fontSize: 9,
            lineHeight: 1,
            color: '#fff',
          }}
        >
          {count > 9 ? '9+' : count}
        </span>
      </span>
    )}
  </div>
);

export const Header: React.FC<HeaderProps> = ({
  onNotificationClick,
  // onProfileClick — no longer used; avatar opens settings directly via Sidebar
  onCreateJournal,
  onBackNavigate,
  showBackButton = false,
  isChangePasswordTab = false,
  children,
  disableSidebar = false,
  showCancelButton = false,
  onCancel,
  isRecordingActive = false,
  onMenuBlocked,
  onOpenConversation,
  onListenInConversation,
  conversationId,
  onViewFileInConversation,
  onFileDeleted,
  isDesktop = false,
  onToggleSidebar,
  onAvatarClick,
}) => {
  const {
    drawerOpen,
    urgentOpen,
    scheduleOpen,
    handleMenuClick,
    handleDrawerClose,
    handleCreateJournal,
    handleBackClick,
    handleOpenUrgent,
    closeAll,
    setUrgentOpen,
    setScheduleOpen,
    shouldShowBack,
  } = useHeader({
    onCreateJournal,
    onBackNavigate,
    showBackButton,
    isChangePasswordTab,
  });

  // Prefetch notes meta in background after initial render
  React.useEffect(() => {
    if (disableSidebar) return;
    const id = (window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 300)))(prefetchNotesMeta);
    return () => (window.cancelIdleCallback ?? clearTimeout)(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [avatarSettingsTab, setAvatarSettingsTab] = React.useState<string | null>(null);
  const { items, unreadCount, fetchNotifications, markRead, markAllRead, dismiss, hasMore, isLoading } =
    useNotifications();

  const handleBellClick = () => {
    const opening = !notificationsOpen;
    setNotificationsOpen(opening);
    if (opening) {
      // Always refresh from server when opening the panel
      fetchNotifications(true);
    }
    onNotificationClick?.();
  };

  const handlePanelOpenChange = (open: boolean) => {
    setNotificationsOpen(open);
  };

  const closeNotifications = React.useCallback(() => setNotificationsOpen(false), []);
  useBackHandler('notifications', notificationsOpen, closeNotifications);

  const { profile } = useAuth();
  const [isUrgentMessageLoading, setIsUrgentMessageLoading] = React.useState(false);

  const handleUrgentMessageSend = async (message: string) => {
    if (!conversationId) {
      console.error('No conversation ID available');
      setUrgentOpen(false);
      return;
    }

    setIsUrgentMessageLoading(true);

    try {
      const response = await smsApi.sendUrgentMessage({
        conversation_id: conversationId,
        message: message,
      });

      if (!response.ok) {
        console.error('Failed to send urgent message:', response.error);
        setIsUrgentMessageLoading(false);
      } else {
        // Keep loading state while navigating to conversation
        setUrgentOpen(false);
        // Navigate to the conversation to see the sent message
        onOpenConversation?.();
        // Reset loading state after a brief delay
        setTimeout(() => {
          setIsUrgentMessageLoading(false);
        }, 500);
      }
    } catch (error) {
      console.error('Failed to send urgent message:', error);
      setIsUrgentMessageLoading(false);
      setUrgentOpen(false);
    }
  };

  const initials =
    `${String(profile?.first_name ?? '')
      .trim()
      .charAt(0)}${String(profile?.last_name ?? '')
      .trim()
      .charAt(0)}`.trim() ||
    (profile?.nickname?.[0]?.toUpperCase() ?? '?');

  return (
    <>
      <HeaderSpacer>
        <HeaderContainer>
          <HamburgerContainer>
            {shouldShowBack ? (
              <IconBtn aria-label="Go back" onClick={handleBackClick}>
                <img src="/close.svg" alt="close" width={44} height={44} draggable={false} />
              </IconBtn>
            ) : isDesktop ? (
              <IconBtn aria-label="Toggle sidebar" onClick={onToggleSidebar}>
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#B7BDCB" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </IconBtn>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <FiMenu
                  style={{
                    color: '#B7BDCB',
                    cursor: isRecordingActive ? 'default' : 'pointer',
                    opacity: isRecordingActive ? 0.3 : 1,
                    transition: 'opacity 150ms ease',
                  }}
                  size={24}
                  onClick={
                    isRecordingActive
                      ? undefined
                      : onMenuBlocked
                        ? onMenuBlocked
                        : handleMenuClick
                  }
                  aria-label="Open menu"
                  aria-disabled={isRecordingActive}
                />
              </div>
            )}
          </HamburgerContainer>

          <>{children}</>

          <RightActions>
            {showCancelButton ? (
              <CancelButton
                type="button"
                onClick={() => {
                  onCancel?.();
                }}
                aria-label="Cancel recording"
              >
                Cancel
              </CancelButton>
            ) : (
              <>
                <IconButton aria-label="Notifications" onClick={handleBellClick}>
                  <NotificationBell count={unreadCount} />
                </IconButton>

                <AppAvatar
                  onClick={onAvatarClick ?? (() => setAvatarSettingsTab('profile'))}
                  userId={String(profile?.id ?? '')}
                  version={(profile as any)?.avatar_version ?? (profile as any)?.updated_at ?? null}
                  hasAvatar={!!profile?.avatar_url}
                  alt="You"
                  initials={initials}
                  width={34}
                  height={34}
                  imgStyle={{ borderRadius: '999px', objectFit: 'cover', cursor: 'pointer' }}
                  fallbackStyle={{ borderRadius: '999px', cursor: 'pointer' }}
                />
              </>
            )}
          </RightActions>
        </HeaderContainer>
      </HeaderSpacer>

      {!disableSidebar && !shouldShowBack && (
        <Sidebar
          open={drawerOpen}
          onClose={handleDrawerClose}
          onCreateJournal={handleCreateJournal}
          onOpenUrgent={handleOpenUrgent}
          openSettingsTab={avatarSettingsTab}
          onSettingsClosed={() => setAvatarSettingsTab(null)}
          {...(onViewFileInConversation ? { onViewFileInConversation } : {})}
          {...(onFileDeleted ? { onFileDeleted } : {})}
        />
      )}

      <NotificationCollection
        open={notificationsOpen}
        onOpenChange={handlePanelOpenChange}
        notifications={items}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onDismiss={dismiss}
        onLoadMore={() => fetchNotifications(false)}
        hasMore={hasMore}
        isLoading={isLoading}
        onOpenConversation={onOpenConversation}
        onListenInConversation={onListenInConversation}
      />

      <UrgentMessageModal
        open={urgentOpen}
        onOpenChange={setUrgentOpen}
        onSend={handleUrgentMessageSend}
        isLoading={isUrgentMessageLoading}
      />

      <ScheduleMeetingModal
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        onCancelAll={closeAll}
        headerHeight={81}
      />
    </>
  );
};
